// LoginScreen — email/password login form with optional 2FA challenge
// and Microsoft SSO entry point. The only top-level component the user
// sees before being authenticated.

import { useEffect, useState } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Btn, Inp, Card } from "./primitives";
import { ForceChangePasswordScreen } from "./ForceChangePasswordScreen";

export const LoginScreen = ({ onLogin = undefined }: { onLogin?: any }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<any>(null);
  const [totpCode, setTotpCode] = useState("");
  const [twoFAError, setTwoFAError] = useState("");
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [forcedSetupData, setForcedSetupData] = useState<any>(null);
  // The short-lived `2fa_setup_required` JWT for the forced-setup flow. Held in
  // component state — NOT in the shared localStorage `kontractai_token` slot,
  // which the login + force-change-password flows also write/clear and which
  // previously got clobbered before /2fa/confirm (the forced-setup login bug).
  const [forcedSetupToken, setForcedSetupToken] = useState<string>("");
  const [forcedConfirmCode, setForcedConfirmCode] = useState("");
  const [forcedConfirmError, setForcedConfirmError] = useState("");
  // Neutral SSO status message (e.g. "enter your email" or "SSO not available for that email").
  // The pre-login org roster is never fetched or rendered — only the email-matched org proceeds.
  const [ssoMsg, setSsoMsg] = useState<string>("");
  const [ssoLoading, setSsoLoading] = useState(false);
  const [requiresPwChange, setRequiresPwChange] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState<{ user: any; token: string } | null>(null);
  const [capturedPassword, setCapturedPassword] = useState<string>("");
  // Deployment edition — fetched once from the public /api/app/config endpoint
  // on mount. LoginScreen renders before any authenticated context is
  // available, so this is a one-time local-state read rather than a context
  // subscription. No firm-specific marketing copy exists today; the hook is
  // in place for later theming.
  const [edition, setEdition] = useState<"firm" | "in_house">("firm");
  useEffect(() => {
    fetch("/api/app/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && (d.edition === "firm" || d.edition === "in_house")) setEdition(d.edition); })
      .catch(() => {});
  }, []);
  void edition;

  // Pre-fill email after successful invite acceptance
  // (Wave 2.5 hotfix: AcceptInvitePage redirects here with ?invite_accepted=1&email=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("invite_accepted") === "1") {
      const e = params.get("email");
      if (e) setEmail(e);
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, "", "/");
      }
    }
  }, []);

  const handleMicrosoftSSO = async () => {
    setSsoMsg("");
    const hint = email ? `?login_hint=${encodeURIComponent(email)}` : "";
    const url = `/api/auth/microsoft${hint}`;
    setSsoLoading(true);
    try {
      const res = await fetch(url, { method: "GET", redirect: "manual" });
      // A configured (or single-tenant) org → the server 302s to Microsoft; proceed.
      if (res.type === "opaqueredirect" || res.status === 0 || (res.status >= 300 && res.status < 400)) {
        window.location.href = url;
        return;
      }
      if (res.status === 200) {
        const body = await res.json().catch(() => ({} as any));
        // No email yet: ask for one — never list organizations.
        if (body && body.needs_email) {
          setSsoMsg('Enter your work email above, then click "Sign in with Microsoft" again.');
          setSsoLoading(false);
          return;
        }
        // Email entered but no organization matches — neutral message, password fallback remains.
        if (body && body.sso_unavailable) {
          setSsoMsg("Single sign-on isn't available for that email address. Please sign in with your password.");
          setSsoLoading(false);
          return;
        }
      }
      // Anything else: fall through to top-level navigation (handles redirects browsers hide).
      window.location.href = url;
    } catch {
      window.location.href = url;
    }
  };

  const handleSubmit = async () => {
    if (!email || !password) { setError("Email and password required"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Login failed"); setLoading(false); return; }
      if (data.requires2FA) { setRequires2FA(true); setTempToken(data.tempToken); setTotpCode(""); setTwoFAError(""); setLoading(false); return; }
      if (data.requires2FASetup) {
        // Carry the setup token in state (NOT the shared localStorage slot) and
        // use it for both /2fa/setup and /2fa/confirm so the password-change /
        // login flows can never clobber it mid-setup.
        setForcedSetupToken(data.token);
        const setupRes = await fetch("/api/auth/2fa/setup", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${data.token}` } });
        if (setupRes.ok) { setForcedSetupData(await setupRes.json()); setRequires2FASetup(true); }
        else { setError("Could not start two-factor setup. Please log in again."); }
        setLoading(false); return;
      }
      localStorage.setItem("kontractai_token", data.token);
      localStorage.setItem("kontractai_user", JSON.stringify(data.user));
      if (data.user.must_change_password) {
        setRequiresPwChange(true);
        setPendingLoginData({ user: data.user, token: data.token });
        setCapturedPassword(password);
        setLoading(false);
        return;
      }
      onLogin?.(data.user, data.token);
    } catch (err: any) { setError("Connection error. Please try again."); }
    setLoading(false);
  };

  const handleVerify2FA = async () => {
    if (!totpCode || totpCode.length < 6) return;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tempToken, totpCode }) });
      const data = await res.json();
      if (data.token) {
        setRequires2FA(false);
        localStorage.setItem("kontractai_token", data.token);
        localStorage.setItem("kontractai_user", JSON.stringify(data.user));
        if (data.user.must_change_password) {
          setRequiresPwChange(true);
          setPendingLoginData({ user: data.user, token: data.token });
          setCapturedPassword(password);
          setLoading(false);
          return;
        }
        onLogin?.(data.user, data.token);
      }
      else { setTwoFAError(data.error || "Invalid code"); }
    } catch (err: any) { setTwoFAError("Connection error"); }
    setLoading(false);
  };

  const handleForcedConfirm = async () => {
    if (forcedConfirmCode.length < 6) return;
    // Use the setup token from state (not localStorage).
    const res = await fetch("/api/auth/2fa/confirm", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${forcedSetupToken}` }, body: JSON.stringify({ code: forcedConfirmCode }) });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.enabled) {
      setRequires2FASetup(false); setForcedSetupData(null); setForcedSetupToken("");
      alert("Two-factor authentication is now enabled. Please log in again with your authenticator code.");
      return;
    }
    // 401 here means the SETUP TOKEN (not the code) was rejected — e.g. it expired.
    // Send the admin back to login to mint a fresh setup token rather than showing
    // a misleading "invalid code" error.
    if (res.status === 401) {
      setRequires2FASetup(false); setForcedSetupData(null); setForcedSetupToken(""); setForcedConfirmCode("");
      setError("Your two-factor setup session expired. Please log in again to restart setup.");
      return;
    }
    setForcedConfirmError(data.error || "Invalid code");
  };

  if (requiresPwChange && pendingLoginData) {
    return (
      <ForceChangePasswordScreen
        user={pendingLoginData.user}
        token={pendingLoginData.token}
        capturedPassword={capturedPassword}
        onSuccess={({ token, user }) => {
          setRequiresPwChange(false);
          setCapturedPassword("");
          setPendingLoginData(null);
          onLogin?.(user, token);
        }}
        onLogout={() => {
          localStorage.removeItem("kontractai_token");
          localStorage.removeItem("kontractai_user");
          setRequiresPwChange(false);
          setCapturedPassword("");
          setPendingLoginData(null);
          setEmail("");
          setPassword("");
        }}
      />
    );
  }

  if (requires2FASetup && forcedSetupData) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#F5F7FA", fontFamily: "'Outfit', 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 420, padding: 32, backgroundColor: "#fff", borderRadius: 12, border: `1px solid ${T.brd}`, boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Set Up Two-Factor Authentication</div>
          <div style={{ fontSize: 12, color: T.txM }}>Your administrator requires 2FA. Complete setup to continue.</div>
        </div>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>Scan with Microsoft Authenticator, Google Authenticator, or any TOTP app:</div>
          <img src={forcedSetupData.qrCode} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
          <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>Manual entry: <code style={{ userSelect: "all", color: T.acc }}>{forcedSetupData.secret}</code></div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.orn, marginBottom: 4 }}>Save your recovery codes now:</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 10, backgroundColor: T.bg3, borderRadius: 6 }}>
            {forcedSetupData.recoveryCodes.map((c: string, i: number) => <div key={i} style={{ fontFamily: "monospace", fontSize: 12, color: T.tx }}>{c}</div>)}
          </div>
          <div style={{ fontSize: 10, color: T.red, marginTop: 4 }}>These codes will not be shown again.</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={forcedConfirmCode} onChange={e => { setForcedConfirmCode(e.target.value.replace(/[^0-9]/g, "")); setForcedConfirmError(""); }} onKeyDown={e => e.key === "Enter" && handleForcedConfirm()} autoFocus style={{ padding: "8px 12px", borderRadius: 6, border: `2px solid ${forcedConfirmError ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 16, width: 140, fontFamily: "monospace", textAlign: "center", letterSpacing: "0.2em" }} />
          <Btn v="primary" onClick={() => { handleForcedConfirm(); }} disabled={forcedConfirmCode.length < 6}>Verify & Enable</Btn>
        </div>
        {forcedConfirmError && <div style={{ color: T.red, fontSize: 11, marginTop: 8, textAlign: "center" }}>{forcedConfirmError}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#F5F7FA", fontFamily: "'Outfit', 'Segoe UI', sans-serif" }}>
      <div style={{ textAlign: "center", width: 380 }}>
        <div style={{ marginBottom: 36 }}>
          <img src="/logo.png" alt="KontractAI" style={{ height: 56, marginBottom: 16 }} />
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", color: T.txD, textTransform: "uppercase" }}>Contract Lifecycle Management</div>
        </div>
        <Card style={{ textAlign: "left", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
          {requires2FA ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.tx, marginBottom: 8 }}>Two-Factor Authentication</div>
              <div style={{ fontSize: 12, color: T.txM, marginBottom: 20 }}>Enter the 6-digit code from your authenticator app</div>
              <input type="text" inputMode="numeric" maxLength={6} value={totpCode} onChange={e => { setTotpCode(e.target.value.replace(/[^0-9A-Fa-f]/g, "")); setTwoFAError(""); }} onKeyDown={e => e.key === "Enter" && handleVerify2FA()} placeholder="000000" autoFocus style={{ width: 180, padding: "12px 16px", fontSize: 24, fontWeight: 700, textAlign: "center", letterSpacing: "0.3em", borderRadius: 8, border: `2px solid ${twoFAError ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "monospace" }} />
              {twoFAError && <div style={{ color: T.red, fontSize: 12, marginTop: 8 }}>{twoFAError}</div>}
              <div style={{ marginTop: 16 }}><Btn v="primary" full onClick={() => { handleVerify2FA(); }} disabled={totpCode.length < 6 || loading}>{loading ? "Verifying..." : "Verify"}</Btn></div>
              <button onClick={() => { setRequires2FA(false); setTempToken(null); setTotpCode(""); }} style={{ background: "none", border: "none", color: T.acc, cursor: "pointer", fontSize: 12, marginTop: 12 }}>Back to Login</button>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 12 }}>You can also enter a recovery code.</div>
            </div>
          ) : (
            <>
              <Inp label="Email" value={email} onChange={setEmail} placeholder="you@company.com" type="email" />
              <Inp label="Password" value={password} onChange={setPassword} placeholder="Enter password" type="password" />
              {error && <div style={{ fontSize: 11, color: T.red, marginBottom: 12, padding: "6px 10px", backgroundColor: T.redS, borderRadius: 4 }}>{error}</div>}
              <Btn v="primary" sz="lg" full onClick={() => { handleSubmit(); }} disabled={loading || !email || !password}>{loading ? "Signing in..." : "Sign In"}</Btn>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
                <div style={{ flex: 1, height: 1, backgroundColor: T.brd }} /><span style={{ fontSize: 11, color: T.txD, fontWeight: 500 }}>or</span><div style={{ flex: 1, height: 1, backgroundColor: T.brd }} />
              </div>
              {ssoMsg && <div style={{ fontSize: 11, color: T.txD, marginBottom: 10, padding: "6px 10px", backgroundColor: T.bg3, borderRadius: 4, textAlign: "center" }}>{ssoMsg}</div>}
              <button onClick={() => { handleMicrosoftSSO(); }} disabled={ssoLoading} style={{ width: "100%", padding: "11px 16px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: "#2F2F2F", color: "#fff", fontSize: 13, fontWeight: 600, cursor: ssoLoading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontFamily: "inherit", opacity: ssoLoading ? 0.7 : 1 }}>
                <svg width="16" height="16" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                {ssoLoading ? "Loading..." : "Sign in with Microsoft"}
              </button>
            </>
          )}
        </Card>
        <p style={{ color: T.txD, fontSize: 10, marginTop: 14 }}>KontractAI v1.0</p>
      </div>
    </div>
  );
};
